/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Appointment Types API
 * API for managing appointment types (afspraaktypen) within an organization.  Appointment types define templates for appointments including: - Default duration for automatic time slot filling - Consult type (diagnostiek/behandeling) for billing code lookup - Whether a report is required after the appointment - Which profession codes are allowed to perform this type  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface AppointmentTypeListResponse {
    'content': Array<AppointmentTypeResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of items
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface AppointmentTypeResponse {
    /**
     * Appointment type unique identifier
     */
    'id': string;
    /**
     * Organization this appointment type belongs to
     */
    'organizationId': string;
    /**
     * Appointment type name (e.g., \"Evaluatie\", \"Intake\")
     */
    'name': string;
    /**
     * Optional description of the appointment type
     */
    'description'?: string | null;
    /**
     * Default duration in minutes for this appointment type
     */
    'defaultDurationMinutes': number;
    /**
     * Whether a report should be created after this appointment type
     */
    'requiresReport': boolean;
    /**
     * Whether this appointment type is active and available for selection
     */
    'isActive': boolean;
    /**
     * MDO (multidisciplinair overleg): clients on appointments of this type are the subjects discussed, not invitees. They receive no invitations or reminders, the appointment is hidden from the client portal, and billing time is distributed across them. Snapshotted onto appointments at creation. 
     */
    'isMdo': boolean;
    /**
     * Optional accent colour as a `#RRGGBB` hex string, picked from the curated preset palette in the UI. The agenda renders it as a left accent strip on events of this type. Decorative only — null means no accent. 
     */
    'color'?: string | null;
    'billingCategory': BillingCategory;
    /**
     * Consult type for NZA service code lookup. Required when billingCategory is INSURED and no fixedServiceCodeId is set, must be null for PRIVATE and NON_BILLABLE. 
     */
    'consultType'?: ConsultType | null;
    /**
     * Price in EUR for PRIVATE billing category appointments. Required when billingCategory is PRIVATE, must be null otherwise. 
     */
    'privatePriceEuro'?: number | null;
    /**
     * A prestatiecode this afspraaktype always books, instead of the one the ZPM decision tree resolves. Only a service code that is `clientInvoiced` (list them with `GET /api/v1/service-codes?clientInvoiced=true`) may be named, and only when billingCategory is INSURED; otherwise the request is a 400 whose Problem Details `errors` names this field. While it is set, consultType is not required. 
     */
    'fixedServiceCodeId'?: string | null;
    /**
     * Profession codes allowed to perform this appointment type. According to the kwaliteitsstatuut, certain session types require specific qualifications. Empty array means all professions are allowed. 
     */
    'allowedProfessionCodes': Array<ProfessionCode>;
    /**
     * Whether an appointment of this type that did not go ahead offers to replace it with an appointment of another type (#3438, #3635). Off for every existing type: an organisation that configures nothing keeps today\'s behaviour. 
     */
    'cancellationReplacementEnabled': boolean;
    /**
     * The appointment type the original appointment may be replaced by. Required while `cancellationReplacementEnabled` is true. Its `defaultDurationMinutes` is what the replacement lasts — there is no separate duration setting. One replacement type serves every triggering outcome. 
     */
    'cancellationReplacementTypeId'?: string | null;
    /**
     * The outcomes that offer the replacement. An outcome outside this list is recorded exactly as it is today. Non-empty while `cancellationReplacementEnabled` is true. 
     */
    'cancellationReplacementTriggers': Array<CancellationReplacementTrigger>;
    /**
     * Declarabele tijd that is not an afspraak *with* the cliënt — uitwerking of a session, a 15-minute e-health contact (#4074). Appointments of this type are absent from the cliëntenportaal and send the cliënt no reminder or confirmation. Read live rather than snapshotted onto the appointment, so ticking it also hides the afspraken that already exist. Says nothing about billing. 
     */
    'hiddenFromClient': boolean;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). The default an afspraak of this type reserves in the agenda; copied onto the appointment at creation, so changing it does not move afspraken already planned. Never part of the session, so never declared. 
     */
    'indirectMinutesBefore': number;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). The default an afspraak of this type reserves in the agenda; copied onto the appointment at creation, so changing it does not move afspraken already planned. Never part of the session, so never declared. 
     */
    'indirectMinutesAfter': number;
    /**
     * Travel time before the session, in minutes (#4817). The default an afspraak of this type reserves in the agenda; copied onto the appointment at creation, so changing it does not move afspraken already planned. Never part of the session, so never declared. 
     */
    'travelMinutesBefore': number;
    /**
     * Travel time after the session, in minutes (#4817). The default an afspraak of this type reserves in the agenda; copied onto the appointment at creation, so changing it does not move afspraken already planned. Never part of the session, so never declared. 
     */
    'travelMinutesAfter': number;
    /**
     * Timestamp when created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}


/**
 * Billing category defining how an appointment type is billed: - INSURED: Billed through VECOZO to health insurers (requires consultType) - PRIVATE: Billed directly to the client (requires privatePriceEuro) - NON_BILLABLE: Not billed at all (e.g., internal meetings, admin time) 
 */

export const BillingCategory = {
    Insured: 'INSURED',
    Private: 'PRIVATE',
    NonBillable: 'NON_BILLABLE'
} as const;

export type BillingCategory = typeof BillingCategory[keyof typeof BillingCategory];


/**
 * An outcome of an afspraak that can offer a replacement appointment (#3438, #3635).  Deliberately **not** `CancellationStatus`: that schema is what `POST /appointments/{id}/cancel` accepts, and a no-show is a different transition (`POST /appointments/{id}/no-show`) that `Appointment.cancel` rejects. Widening the shared name would widen the cancel contract with it — so the trigger list carries a name of its own, which is also what it now is: outcomes, not only cancellations. - CANCELLED: Cancelled at or after the start time - CANCELLED_ADVANCE: Timely cancellation (at least the organisation\'s late-cancellation window (default 24 hours) before start) - CANCELLED_LATE: Late cancellation (within the organisation\'s late-cancellation window (default 24 hours) of the start) - NO_SHOW: The client did not turn up and did not cancel 
 */

export const CancellationReplacementTrigger = {
    Cancelled: 'CANCELLED',
    CancelledAdvance: 'CANCELLED_ADVANCE',
    CancelledLate: 'CANCELLED_LATE',
    NoShow: 'NO_SHOW'
} as const;

export type CancellationReplacementTrigger = typeof CancellationReplacementTrigger[keyof typeof CancellationReplacementTrigger];


/**
 * Consult type for NZA service code lookup: - DIAGNOSTIEK: Diagnostic session - BEHANDELING: Treatment session - VERBLIJF: Residential stay (verblijfsdagen) - uses care intensity for billing code lookup 
 */

export const ConsultType = {
    Diagnostiek: 'DIAGNOSTIEK',
    Behandeling: 'BEHANDELING',
    Verblijf: 'VERBLIJF'
} as const;

export type ConsultType = typeof ConsultType[keyof typeof ConsultType];


export interface CreateAppointmentTypeRequest {
    /**
     * Appointment type name
     */
    'name': string;
    /**
     * Optional description
     */
    'description'?: string | null;
    /**
     * Default duration in minutes
     */
    'defaultDurationMinutes'?: number;
    /**
     * Whether a report is required
     */
    'requiresReport'?: boolean;
    /**
     * Whether the type is active
     */
    'isActive'?: boolean;
    /**
     * MDO (multidisciplinair overleg): clients are subjects, not invitees. Omitted means false. 
     */
    'isMdo'?: boolean;
    /**
     * Optional accent colour as a `#RRGGBB` hex string (null = no accent)
     */
    'color'?: string | null;
    'billingCategory': BillingCategory;
    /**
     * Consult type for NZA service code lookup. Required when billingCategory is INSURED and no fixedServiceCodeId is set, must be null for PRIVATE and NON_BILLABLE. 
     */
    'consultType'?: ConsultType | null;
    /**
     * Price in EUR for PRIVATE billing category appointments. Required when billingCategory is PRIVATE, must be null otherwise. 
     */
    'privatePriceEuro'?: number | null;
    /**
     * A prestatiecode this afspraaktype always books, instead of the one the ZPM decision tree resolves. Only a service code that is `clientInvoiced` (list them with `GET /api/v1/service-codes?clientInvoiced=true`) may be named, and only when billingCategory is INSURED; otherwise the request is a 400 whose Problem Details `errors` names this field. While it is set, consultType is not required. 
     */
    'fixedServiceCodeId'?: string | null;
    /**
     * Profession codes allowed to perform this appointment type. Empty array (default) means all professions are allowed. 
     */
    'allowedProfessionCodes'?: Array<ProfessionCode>;
    /**
     * Whether an appointment of this type that did not go ahead offers a replacement (#3438). Omitted means off — the type behaves exactly as types did before this setting. 
     */
    'cancellationReplacementEnabled'?: boolean | null;
    /**
     * The appointment type the original appointment may be replaced by. Required while `cancellationReplacementEnabled` is true. 
     */
    'cancellationReplacementTypeId'?: string | null;
    /**
     * The outcomes that offer the replacement. Non-empty while `cancellationReplacementEnabled` is true. 
     */
    'cancellationReplacementTriggers'?: Array<CancellationReplacementTrigger> | null;
    /**
     * Hide appointments of this type from the cliëntenportaal and send the cliënt no reminder or confirmation (#4074). Omitted means visible — nothing changes for a type configured before this setting existed. Says nothing about billing. 
     */
    'hiddenFromClient'?: boolean | null;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). Omitted means 0 — no time reserved. 
     */
    'indirectMinutesBefore'?: number | null;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). Omitted means 0 — no time reserved. 
     */
    'indirectMinutesAfter'?: number | null;
    /**
     * Travel time before the session, in minutes (#4817). Omitted means 0 — no time reserved. 
     */
    'travelMinutesBefore'?: number | null;
    /**
     * Travel time after the session, in minutes (#4817). Omitted means 0 — no time reserved. 
     */
    'travelMinutesAfter'?: number | null;
}


export interface CreateReminderRuleRequest {
    'kind': ReminderKind;
    /**
     * Number of hours before the appointment to fire. Must be > 0 for REMINDER and exactly 0 for CONFIRMATION. 
     */
    'hoursBefore': number;
    /**
     * Whether the rule is active (defaults to true)
     */
    'enabled'?: boolean;
    /**
     * The practice\'s own text for the mail (#4685), in place of the default intro and contact paragraphs; the subject, the date/time block and the signature stay fixed. Paragraphs are separated by a blank line, an `https://` URL becomes a link, and `{{key}}` is replaced by a value from `GET .../reminder-rules/placeholders`. Nothing else is interpreted: the text is never evaluated as a template or as HTML. A `{{key}}` outside that list is a `400` whose `errors` name it. A blank text is stored as null. Omitted or null sends the default text. 
     */
    'message'?: string | null;
}


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * NZA profession code for billing service code lookup: - ARTS_BIG3: Arts artikel 3 Wet BIG - ARTS_BIG14: Arts artikel 14 Wet BIG - GZ_PSYCH: GZ-psycholoog - KLIN_PSYCH: Klinisch psycholoog - PSYCHOTHER: Psychotherapeut - VERPL: Verpleegkundige - VERPL_SPEC: Verpleegkundig specialist - OVERIG: Overig 
 */

export const ProfessionCode = {
    ArtsBig3: 'ARTS_BIG3',
    ArtsBig14: 'ARTS_BIG14',
    GzPsych: 'GZ_PSYCH',
    KlinPsych: 'KLIN_PSYCH',
    Psychother: 'PSYCHOTHER',
    Verpl: 'VERPL',
    VerplSpec: 'VERPL_SPEC',
    Overig: 'OVERIG'
} as const;

export type ProfessionCode = typeof ProfessionCode[keyof typeof ProfessionCode];


/**
 * Kind of reminder rule: - REMINDER: Reminder sent some number of hours before the appointment - CONFIRMATION: Confirmation sent immediately on booking (hoursBefore must be 0) 
 */

export const ReminderKind = {
    Reminder: 'REMINDER',
    Confirmation: 'CONFIRMATION'
} as const;

export type ReminderKind = typeof ReminderKind[keyof typeof ReminderKind];


export interface ReminderMessagePlaceholder {
    /**
     * What goes between `{{` and `}}`
     */
    'key': string;
    /**
     * What the value is, in Dutch
     */
    'description': string;
    /**
     * The stand-in the preview renders
     */
    'example': string;
}
export interface ReminderMessagePlaceholderListResponse {
    'content': Array<ReminderMessagePlaceholder>;
}
export interface ReminderMessagePreviewRequest {
    'kind': ReminderKind;
    /**
     * The unsaved text to preview; null previews the default text.
     */
    'message'?: string | null;
}


export interface ReminderMessagePreviewResponse {
    /**
     * The mail\'s subject, which the text does not change
     */
    'subject': string;
    /**
     * The mail\'s HTML body, as it would be sent
     */
    'html': string;
}
export interface ReminderRuleListResponse {
    'content': Array<ReminderRuleResponse>;
}
export interface ReminderRuleResponse {
    'id': string;
    'organizationId': string;
    'appointmentTypeId': string;
    'kind': ReminderKind;
    /**
     * Number of hours before the appointment to fire. Must be > 0 for REMINDER and exactly 0 for CONFIRMATION. 
     */
    'hoursBefore': number;
    /**
     * Whether the rule is active
     */
    'enabled': boolean;
    /**
     * The practice\'s own text for the mail; null sends the default text.
     */
    'message'?: string | null;
    'createdAt': number;
    'updatedAt': number;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateAppointmentTypeRequest {
    /**
     * Appointment type name
     */
    'name'?: string | null;
    /**
     * Optional description
     */
    'description'?: string | null;
    /**
     * Default duration in minutes
     */
    'defaultDurationMinutes'?: number | null;
    /**
     * Whether a report is required
     */
    'requiresReport'?: boolean | null;
    /**
     * Whether the type is active
     */
    'isActive'?: boolean | null;
    /**
     * MDO (multidisciplinair overleg): clients are subjects, not invitees. Only affects appointments created after the change. 
     */
    'isMdo'?: boolean | null;
    /**
     * Optional accent colour as a `#RRGGBB` hex string (explicit null clears it)
     */
    'color'?: string | null;
    'billingCategory'?: BillingCategory | null;
    /**
     * Required when billingCategory is INSURED and no fixedServiceCodeId is set
     */
    'consultType'?: ConsultType | null;
    /**
     * Required when billingCategory is PRIVATE
     */
    'privatePriceEuro'?: number | null;
    /**
     * A `clientInvoiced` prestatiecode this afspraaktype always books (explicit null clears it, omitted leaves it unchanged). Only allowed while billingCategory is INSURED. 
     */
    'fixedServiceCodeId'?: string | null;
    /**
     * Profession codes allowed (empty array = all allowed)
     */
    'allowedProfessionCodes'?: Array<ProfessionCode> | null;
    /**
     * Whether an appointment of this type that did not go ahead offers a replacement (#3438) 
     */
    'cancellationReplacementEnabled'?: boolean | null;
    /**
     * The appointment type the original appointment may be replaced by (explicit null clears it). Required while the behaviour is enabled. 
     */
    'cancellationReplacementTypeId'?: string | null;
    /**
     * The outcomes that offer the replacement. Non-empty while the behaviour is enabled. 
     */
    'cancellationReplacementTriggers'?: Array<CancellationReplacementTrigger> | null;
    /**
     * Hide appointments of this type from the cliëntenportaal and send the cliënt no reminder or confirmation (#4074). Read live, so flipping it also hides — or reveals — the afspraken that already exist. Says nothing about billing. 
     */
    'hiddenFromClient'?: boolean | null;
    /**
     * Indirect time (voorbereiding) before the session, in minutes (#4817). Omitted or null leaves it unchanged. Afspraken already planned keep their own copy. 
     */
    'indirectMinutesBefore'?: number | null;
    /**
     * Indirect time (verslaglegging) after the session, in minutes (#4817). Omitted or null leaves it unchanged. Afspraken already planned keep their own copy. 
     */
    'indirectMinutesAfter'?: number | null;
    /**
     * Travel time before the session, in minutes (#4817). Omitted or null leaves it unchanged. Afspraken already planned keep their own copy. 
     */
    'travelMinutesBefore'?: number | null;
    /**
     * Travel time after the session, in minutes (#4817). Omitted or null leaves it unchanged. Afspraken already planned keep their own copy. 
     */
    'travelMinutesAfter'?: number | null;
}


/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateReminderRuleRequest {
    'kind'?: ReminderKind | null;
    'hoursBefore'?: number | null;
    'enabled'?: boolean | null;
    /**
     * The practice\'s own text for the mail (#4685), in place of the default intro and contact paragraphs; the subject, the date/time block and the signature stay fixed. Paragraphs are separated by a blank line, an `https://` URL becomes a link, and `{{key}}` is replaced by a value from `GET .../reminder-rules/placeholders`. Nothing else is interpreted: the text is never evaluated as a template or as HTML. A `{{key}}` outside that list is a `400` whose `errors` name it. A blank text is stored as null. Omitted leaves the text as it is; an explicit null restores the default text. 
     */
    'message'?: string | null;
}



/**
 * AppointmentTypesApi - axios parameter creator
 */
export const AppointmentTypesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new appointment type (requires appointment_types.create permission)
         * @summary Create appointment type
         * @param {CreateAppointmentTypeRequest} createAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAppointmentType: async (createAppointmentTypeRequest: CreateAppointmentTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createAppointmentTypeRequest' is not null or undefined
            assertParamExists('createAppointmentType', 'createAppointmentTypeRequest', createAppointmentTypeRequest)
            const localVarPath = `/api/v1/appointment-types`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createAppointmentTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete an appointment type (requires appointment_types.delete permission). Cannot delete if appointment type is in use by existing appointments.  A refusal is a 409 whose `code` (and problem `type`) names which of the two it is, so a client can offer the right way out rather than matching the `detail` prose:  - `appointment-type-in-use` — appointments still name the type. Carries   `appointmentCount`. Deactivating the type (`isActive: false`) is what removes it   from the agenda composer while past appointments keep it. - `appointment-type-is-cancellation-replacement` — another appointment type creates   its replacement appointment with this one; that type has to be changed first. 
         * @summary Delete appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointmentType: async (appointmentTypeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('deleteAppointmentType', 'appointmentTypeId', appointmentTypeId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns an appointment type by ID
         * @summary Get appointment type by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentTypeById: async (appointmentTypeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('getAppointmentTypeById', 'appointmentTypeId', appointmentTypeId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all appointment types for the authenticated user\'s organization
         * @summary List appointment types
         * @param {boolean} [activeOnly] If true, only return active appointment types
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentTypes: async (activeOnly?: boolean, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/appointment-types`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (activeOnly !== undefined) {
                localVarQueryParameter['activeOnly'] = activeOnly;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update an appointment type (requires appointment_types.update permission)
         * @summary Update appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {UpdateAppointmentTypeRequest} updateAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentType: async (appointmentTypeId: string, updateAppointmentTypeRequest: UpdateAppointmentTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('updateAppointmentType', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'updateAppointmentTypeRequest' is not null or undefined
            assertParamExists('updateAppointmentType', 'updateAppointmentTypeRequest', updateAppointmentTypeRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAppointmentTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentTypesApi - functional programming interface
 */
export const AppointmentTypesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentTypesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new appointment type (requires appointment_types.create permission)
         * @summary Create appointment type
         * @param {CreateAppointmentTypeRequest} createAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createAppointmentType(createAppointmentTypeRequest: CreateAppointmentTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createAppointmentType(createAppointmentTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypesApi.createAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Delete an appointment type (requires appointment_types.delete permission). Cannot delete if appointment type is in use by existing appointments.  A refusal is a 409 whose `code` (and problem `type`) names which of the two it is, so a client can offer the right way out rather than matching the `detail` prose:  - `appointment-type-in-use` — appointments still name the type. Carries   `appointmentCount`. Deactivating the type (`isActive: false`) is what removes it   from the agenda composer while past appointments keep it. - `appointment-type-is-cancellation-replacement` — another appointment type creates   its replacement appointment with this one; that type has to be changed first. 
         * @summary Delete appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteAppointmentType(appointmentTypeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteAppointmentType(appointmentTypeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypesApi.deleteAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns an appointment type by ID
         * @summary Get appointment type by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAppointmentTypeById(appointmentTypeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAppointmentTypeById(appointmentTypeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypesApi.getAppointmentTypeById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all appointment types for the authenticated user\'s organization
         * @summary List appointment types
         * @param {boolean} [activeOnly] If true, only return active appointment types
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentTypes(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentTypes(activeOnly, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypesApi.listAppointmentTypes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update an appointment type (requires appointment_types.update permission)
         * @summary Update appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {UpdateAppointmentTypeRequest} updateAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAppointmentType(appointmentTypeId: string, updateAppointmentTypeRequest: UpdateAppointmentTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAppointmentType(appointmentTypeId, updateAppointmentTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypesApi.updateAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentTypesApi - factory interface
 */
export const AppointmentTypesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentTypesApiFp(configuration)
    return {
        /**
         * Create a new appointment type (requires appointment_types.create permission)
         * @summary Create appointment type
         * @param {CreateAppointmentTypeRequest} createAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createAppointmentType(createAppointmentTypeRequest: CreateAppointmentTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeResponse> {
            return localVarFp.createAppointmentType(createAppointmentTypeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete an appointment type (requires appointment_types.delete permission). Cannot delete if appointment type is in use by existing appointments.  A refusal is a 409 whose `code` (and problem `type`) names which of the two it is, so a client can offer the right way out rather than matching the `detail` prose:  - `appointment-type-in-use` — appointments still name the type. Carries   `appointmentCount`. Deactivating the type (`isActive: false`) is what removes it   from the agenda composer while past appointments keep it. - `appointment-type-is-cancellation-replacement` — another appointment type creates   its replacement appointment with this one; that type has to be changed first. 
         * @summary Delete appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteAppointmentType(appointmentTypeId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteAppointmentType(appointmentTypeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns an appointment type by ID
         * @summary Get appointment type by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAppointmentTypeById(appointmentTypeId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeResponse> {
            return localVarFp.getAppointmentTypeById(appointmentTypeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all appointment types for the authenticated user\'s organization
         * @summary List appointment types
         * @param {boolean} [activeOnly] If true, only return active appointment types
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentTypes(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeListResponse> {
            return localVarFp.listAppointmentTypes(activeOnly, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Update an appointment type (requires appointment_types.update permission)
         * @summary Update appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {UpdateAppointmentTypeRequest} updateAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentType(appointmentTypeId: string, updateAppointmentTypeRequest: UpdateAppointmentTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeResponse> {
            return localVarFp.updateAppointmentType(appointmentTypeId, updateAppointmentTypeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentTypesApi - object-oriented interface
 */
export class AppointmentTypesApi extends BaseAPI {
    /**
     * Create a new appointment type (requires appointment_types.create permission)
     * @summary Create appointment type
     * @param {CreateAppointmentTypeRequest} createAppointmentTypeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createAppointmentType(createAppointmentTypeRequest: CreateAppointmentTypeRequest, options?: RawAxiosRequestConfig) {
        return AppointmentTypesApiFp(this.configuration).createAppointmentType(createAppointmentTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete an appointment type (requires appointment_types.delete permission). Cannot delete if appointment type is in use by existing appointments.  A refusal is a 409 whose `code` (and problem `type`) names which of the two it is, so a client can offer the right way out rather than matching the `detail` prose:  - `appointment-type-in-use` — appointments still name the type. Carries   `appointmentCount`. Deactivating the type (`isActive: false`) is what removes it   from the agenda composer while past appointments keep it. - `appointment-type-is-cancellation-replacement` — another appointment type creates   its replacement appointment with this one; that type has to be changed first. 
     * @summary Delete appointment type
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteAppointmentType(appointmentTypeId: string, options?: RawAxiosRequestConfig) {
        return AppointmentTypesApiFp(this.configuration).deleteAppointmentType(appointmentTypeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns an appointment type by ID
     * @summary Get appointment type by ID
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAppointmentTypeById(appointmentTypeId: string, options?: RawAxiosRequestConfig) {
        return AppointmentTypesApiFp(this.configuration).getAppointmentTypeById(appointmentTypeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all appointment types for the authenticated user\'s organization
     * @summary List appointment types
     * @param {boolean} [activeOnly] If true, only return active appointment types
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAppointmentTypes(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return AppointmentTypesApiFp(this.configuration).listAppointmentTypes(activeOnly, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update an appointment type (requires appointment_types.update permission)
     * @summary Update appointment type
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {UpdateAppointmentTypeRequest} updateAppointmentTypeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAppointmentType(appointmentTypeId: string, updateAppointmentTypeRequest: UpdateAppointmentTypeRequest, options?: RawAxiosRequestConfig) {
        return AppointmentTypesApiFp(this.configuration).updateAppointmentType(appointmentTypeId, updateAppointmentTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ReminderRulesApi - axios parameter creator
 */
export const ReminderRulesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new reminder rule for an appointment type. Requires the `appointment_types.update` permission. 
         * @summary Create reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {CreateReminderRuleRequest} createReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReminderRule: async (appointmentTypeId: string, createReminderRuleRequest: CreateReminderRuleRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('createReminderRule', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'createReminderRuleRequest' is not null or undefined
            assertParamExists('createReminderRule', 'createReminderRuleRequest', createReminderRuleRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createReminderRuleRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Requires the `appointment_types.update` permission.
         * @summary Delete reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteReminderRule: async (appointmentTypeId: string, ruleId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('deleteReminderRule', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('deleteReminderRule', 'ruleId', ruleId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules/{ruleId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)))
                .replace(`{${"ruleId"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Requires the `appointment_types.read` permission.
         * @summary Get a reminder rule by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReminderRule: async (appointmentTypeId: string, ruleId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('getReminderRule', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('getReminderRule', 'ruleId', ruleId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules/{ruleId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)))
                .replace(`{${"ruleId"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The `{{key}}` values a practice may write into a reminder rule\'s `message` (#4685), each with a Dutch description and the example the preview renders. A `{{key}}` outside this list is refused on save. Requires the `appointment_types.read` permission. 
         * @summary List the placeholders a reminder text may use
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReminderMessagePlaceholders: async (appointmentTypeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('listReminderMessagePlaceholders', 'appointmentTypeId', appointmentTypeId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules/placeholders`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all reminder/confirmation rules configured for the given appointment type. Requires the `appointment_types.read` permission. 
         * @summary List reminder rules for an appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReminderRules: async (appointmentTypeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('listReminderRules', 'appointmentTypeId', appointmentTypeId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Renders the reminder or confirmation mail for an unsaved `message` exactly as it would be sent, with each placeholder\'s example value and the appointment type\'s own name. The subject and the date/time block are fixed; the text replaces only the intro and contact paragraphs. A null `message` previews the default text. Requires the `appointment_types.update` permission. 
         * @summary Preview the mail a reminder rule would send
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {ReminderMessagePreviewRequest} reminderMessagePreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReminderMessage: async (appointmentTypeId: string, reminderMessagePreviewRequest: ReminderMessagePreviewRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('previewReminderMessage', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'reminderMessagePreviewRequest' is not null or undefined
            assertParamExists('previewReminderMessage', 'reminderMessagePreviewRequest', reminderMessagePreviewRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules/preview`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reminderMessagePreviewRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update an existing reminder rule. Requires the `appointment_types.update` permission. 
         * @summary Update reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {UpdateReminderRuleRequest} updateReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateReminderRule: async (appointmentTypeId: string, ruleId: string, updateReminderRuleRequest: UpdateReminderRuleRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('updateReminderRule', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'ruleId' is not null or undefined
            assertParamExists('updateReminderRule', 'ruleId', ruleId)
            // verify required parameter 'updateReminderRuleRequest' is not null or undefined
            assertParamExists('updateReminderRule', 'updateReminderRuleRequest', updateReminderRuleRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/reminder-rules/{ruleId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)))
                .replace(`{${"ruleId"}}`, encodeURIComponent(String(ruleId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateReminderRuleRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReminderRulesApi - functional programming interface
 */
export const ReminderRulesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReminderRulesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new reminder rule for an appointment type. Requires the `appointment_types.update` permission. 
         * @summary Create reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {CreateReminderRuleRequest} createReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createReminderRule(appointmentTypeId: string, createReminderRuleRequest: CreateReminderRuleRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderRuleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createReminderRule(appointmentTypeId, createReminderRuleRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.createReminderRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Requires the `appointment_types.update` permission.
         * @summary Delete reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteReminderRule(appointmentTypeId, ruleId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.deleteReminderRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Requires the `appointment_types.read` permission.
         * @summary Get a reminder rule by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderRuleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getReminderRule(appointmentTypeId, ruleId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.getReminderRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The `{{key}}` values a practice may write into a reminder rule\'s `message` (#4685), each with a Dutch description and the example the preview renders. A `{{key}}` outside this list is refused on save. Requires the `appointment_types.read` permission. 
         * @summary List the placeholders a reminder text may use
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReminderMessagePlaceholders(appointmentTypeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderMessagePlaceholderListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReminderMessagePlaceholders(appointmentTypeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.listReminderMessagePlaceholders']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all reminder/confirmation rules configured for the given appointment type. Requires the `appointment_types.read` permission. 
         * @summary List reminder rules for an appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listReminderRules(appointmentTypeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderRuleListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listReminderRules(appointmentTypeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.listReminderRules']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Renders the reminder or confirmation mail for an unsaved `message` exactly as it would be sent, with each placeholder\'s example value and the appointment type\'s own name. The subject and the date/time block are fixed; the text replaces only the intro and contact paragraphs. A null `message` previews the default text. Requires the `appointment_types.update` permission. 
         * @summary Preview the mail a reminder rule would send
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {ReminderMessagePreviewRequest} reminderMessagePreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async previewReminderMessage(appointmentTypeId: string, reminderMessagePreviewRequest: ReminderMessagePreviewRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderMessagePreviewResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.previewReminderMessage(appointmentTypeId, reminderMessagePreviewRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.previewReminderMessage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update an existing reminder rule. Requires the `appointment_types.update` permission. 
         * @summary Update reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {UpdateReminderRuleRequest} updateReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateReminderRule(appointmentTypeId: string, ruleId: string, updateReminderRuleRequest: UpdateReminderRuleRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ReminderRuleResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateReminderRule(appointmentTypeId, ruleId, updateReminderRuleRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReminderRulesApi.updateReminderRule']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReminderRulesApi - factory interface
 */
export const ReminderRulesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReminderRulesApiFp(configuration)
    return {
        /**
         * Create a new reminder rule for an appointment type. Requires the `appointment_types.update` permission. 
         * @summary Create reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {CreateReminderRuleRequest} createReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createReminderRule(appointmentTypeId: string, createReminderRuleRequest: CreateReminderRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReminderRuleResponse> {
            return localVarFp.createReminderRule(appointmentTypeId, createReminderRuleRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Requires the `appointment_types.update` permission.
         * @summary Delete reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteReminderRule(appointmentTypeId, ruleId, options).then((request) => request(axios, basePath));
        },
        /**
         * Requires the `appointment_types.read` permission.
         * @summary Get a reminder rule by ID
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig): AxiosPromise<ReminderRuleResponse> {
            return localVarFp.getReminderRule(appointmentTypeId, ruleId, options).then((request) => request(axios, basePath));
        },
        /**
         * The `{{key}}` values a practice may write into a reminder rule\'s `message` (#4685), each with a Dutch description and the example the preview renders. A `{{key}}` outside this list is refused on save. Requires the `appointment_types.read` permission. 
         * @summary List the placeholders a reminder text may use
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReminderMessagePlaceholders(appointmentTypeId: string, options?: RawAxiosRequestConfig): AxiosPromise<ReminderMessagePlaceholderListResponse> {
            return localVarFp.listReminderMessagePlaceholders(appointmentTypeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all reminder/confirmation rules configured for the given appointment type. Requires the `appointment_types.read` permission. 
         * @summary List reminder rules for an appointment type
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listReminderRules(appointmentTypeId: string, options?: RawAxiosRequestConfig): AxiosPromise<ReminderRuleListResponse> {
            return localVarFp.listReminderRules(appointmentTypeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Renders the reminder or confirmation mail for an unsaved `message` exactly as it would be sent, with each placeholder\'s example value and the appointment type\'s own name. The subject and the date/time block are fixed; the text replaces only the intro and contact paragraphs. A null `message` previews the default text. Requires the `appointment_types.update` permission. 
         * @summary Preview the mail a reminder rule would send
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {ReminderMessagePreviewRequest} reminderMessagePreviewRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        previewReminderMessage(appointmentTypeId: string, reminderMessagePreviewRequest: ReminderMessagePreviewRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReminderMessagePreviewResponse> {
            return localVarFp.previewReminderMessage(appointmentTypeId, reminderMessagePreviewRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Update an existing reminder rule. Requires the `appointment_types.update` permission. 
         * @summary Update reminder rule
         * @param {string} appointmentTypeId Appointment type UUID
         * @param {string} ruleId Reminder rule UUID
         * @param {UpdateReminderRuleRequest} updateReminderRuleRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateReminderRule(appointmentTypeId: string, ruleId: string, updateReminderRuleRequest: UpdateReminderRuleRequest, options?: RawAxiosRequestConfig): AxiosPromise<ReminderRuleResponse> {
            return localVarFp.updateReminderRule(appointmentTypeId, ruleId, updateReminderRuleRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ReminderRulesApi - object-oriented interface
 */
export class ReminderRulesApi extends BaseAPI {
    /**
     * Create a new reminder rule for an appointment type. Requires the `appointment_types.update` permission. 
     * @summary Create reminder rule
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {CreateReminderRuleRequest} createReminderRuleRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createReminderRule(appointmentTypeId: string, createReminderRuleRequest: CreateReminderRuleRequest, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).createReminderRule(appointmentTypeId, createReminderRuleRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Requires the `appointment_types.update` permission.
     * @summary Delete reminder rule
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {string} ruleId Reminder rule UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).deleteReminderRule(appointmentTypeId, ruleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Requires the `appointment_types.read` permission.
     * @summary Get a reminder rule by ID
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {string} ruleId Reminder rule UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getReminderRule(appointmentTypeId: string, ruleId: string, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).getReminderRule(appointmentTypeId, ruleId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The `{{key}}` values a practice may write into a reminder rule\'s `message` (#4685), each with a Dutch description and the example the preview renders. A `{{key}}` outside this list is refused on save. Requires the `appointment_types.read` permission. 
     * @summary List the placeholders a reminder text may use
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReminderMessagePlaceholders(appointmentTypeId: string, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).listReminderMessagePlaceholders(appointmentTypeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all reminder/confirmation rules configured for the given appointment type. Requires the `appointment_types.read` permission. 
     * @summary List reminder rules for an appointment type
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listReminderRules(appointmentTypeId: string, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).listReminderRules(appointmentTypeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Renders the reminder or confirmation mail for an unsaved `message` exactly as it would be sent, with each placeholder\'s example value and the appointment type\'s own name. The subject and the date/time block are fixed; the text replaces only the intro and contact paragraphs. A null `message` previews the default text. Requires the `appointment_types.update` permission. 
     * @summary Preview the mail a reminder rule would send
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {ReminderMessagePreviewRequest} reminderMessagePreviewRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public previewReminderMessage(appointmentTypeId: string, reminderMessagePreviewRequest: ReminderMessagePreviewRequest, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).previewReminderMessage(appointmentTypeId, reminderMessagePreviewRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update an existing reminder rule. Requires the `appointment_types.update` permission. 
     * @summary Update reminder rule
     * @param {string} appointmentTypeId Appointment type UUID
     * @param {string} ruleId Reminder rule UUID
     * @param {UpdateReminderRuleRequest} updateReminderRuleRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateReminderRule(appointmentTypeId: string, ruleId: string, updateReminderRuleRequest: UpdateReminderRuleRequest, options?: RawAxiosRequestConfig) {
        return ReminderRulesApiFp(this.configuration).updateReminderRule(appointmentTypeId, ruleId, updateReminderRuleRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



